import { ApolloClient, InMemoryCache, createHttpLink, split, from, Observable } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { getMainDefinition } from '@apollo/client/utilities'

// Vite環境変数
const GRAPHQL_URL = import.meta.env.VITE_GRAPHQL_URL || 'http://localhost:8000/graphql'
const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8000/graphql'

// メインGraphQLエンドポイント
const httpLink = createHttpLink({
  uri: GRAPHQL_URL,
  credentials: 'include', // Cookie を送信
})

// デバッグ用リンク
const debugLink = setContext((operation, { headers }) => {
  if (import.meta.env.DEV) {
    console.log('[GraphQL Client] Making request:', operation.operationName)
    console.log('[GraphQL Client] Current cookies:', document.cookie)
  }

  return { headers }
})

// CSRFトークンを取得するヘルパー関数
const getCsrfTokenForWs = () => {
  // まずlocalStorageから試す
  const token = localStorage.getItem('csrfToken')
  if (token) {
    return token
  }

  // cookieから取得する場合
  if (typeof document !== 'undefined') {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === 'csrf_token') {
        return decodeURIComponent(value)
      }
    }
  }

  return null
}

// WebSocket Link（Subscriptions用）
const wsLink = new GraphQLWsLink(
  createClient({
    url: WS_URL,
    // connectionParamsを関数にして、接続時に動的に認証情報を取得
    connectionParams: () => {
      const csrfToken = getCsrfTokenForWs()
      if (import.meta.env.DEV) {
        console.log('[WebSocket] connectionParams - CSRF token:', csrfToken ? 'present' : 'missing')
      }
      return {
        // CSRFトークンをセッション識別に使用
        csrfToken: csrfToken,
      }
    },
    // 接続イベントのデバッグ
    on: {
      connected: () => {
        if (import.meta.env.DEV) {
          console.log('[WebSocket] Connected successfully')
        }
      },
      closed: (event) => {
        if (import.meta.env.DEV) {
          console.log('[WebSocket] Connection closed:', event)
        }
      },
      error: (error) => {
        if (import.meta.env.DEV) {
          console.error('[WebSocket] Connection error:', error)
        }
      },
    },
  })
)

// 認証リンク（CSRFトークンを含む）
const authLink = setContext((_, { headers }) => {
  // CSRFトークンをlocalStorageまたはcookieから取得
  const getCsrfToken = () => {
    // まずlocalStorageから試す
    const token = localStorage.getItem('csrfToken')
    if (token) {
      if (import.meta.env.DEV) {
        console.log('[GraphQL Client] Using CSRF token from localStorage')
      }
      return token
    }

    // cookieから取得する場合
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === 'csrf_token') {
        if (import.meta.env.DEV) {
          console.log('[GraphQL Client] Using CSRF token from cookie')
        }
        return decodeURIComponent(value)
      }
    }

    if (import.meta.env.DEV) {
      console.warn('[GraphQL Client] No CSRF token found')
    }
    return null
  }

  const csrfToken = getCsrfToken()

  return {
    headers: {
      ...headers,
      ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
    },
  }
})

// CSRF retry link: server 側 csrf token rotation で stale になった localStorage を
// 自動 refresh + 1 回だけ retry。 GraphQL error extensions.code === 'CSRF_ERROR'
// (= "CSRF token missing" / "Invalid CSRF token") を検知して sessionInfo で
// fresh token 取得 → localStorage 更新 → header 差し替えて forward(operation)。
// 同じ op を 1 度だけ retry (= ctx.csrfRetried flag で無限ループ防止)。
const csrfRetryLink = onError(({ graphQLErrors, operation, forward }) => {
  if (!graphQLErrors) return undefined
  const isCsrf = graphQLErrors.some((e) => (e.extensions as any)?.code === 'CSRF_ERROR')
  if (!isCsrf) return undefined
  const ctx = operation.getContext() as any
  if (ctx.csrfRetried) return undefined

  return new Observable((observer) => {
    fetch(GRAPHQL_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ query: '{ sessionInfo { csrfToken } }' }),
    })
      .then((r) => r.json())
      .then((j) => {
        const fresh = j?.data?.sessionInfo?.csrfToken
        if (!fresh) {
          observer.error(new Error('CSRF refresh failed: no token in sessionInfo'))
          return
        }
        localStorage.setItem('csrfToken', fresh)
        operation.setContext({
          ...ctx,
          csrfRetried: true,
          headers: { ...(ctx.headers || {}), 'X-CSRF-Token': fresh },
        })
        const sub = forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        })
        return () => sub.unsubscribe()
      })
      .catch((e) => observer.error(e))
  })
})

// HTTPとWebSocketを使い分けるリンク
const routingLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  from([csrfRetryLink, debugLink, authLink, httpLink]),
)

// Apollo Client インスタンス
export const apolloClient = new ApolloClient({
  link: routingLink,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // キャッシュポリシーをここで定義
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
})
